import { useCallback, useState } from 'react';
import { useApiCall } from '@/hooks/useApiCall';
import { useApiClient } from '@/hooks/useApiClient';
import { influxFetchSeries } from '@/features/signals/api/SignalsApiClient';
import type { SignalsSeriesPayload, InfluxSeriesQuery } from '@/features/signals/type/SignalsTypes';
import type { BaseResponse } from '@/lib/http/HttpClient';

type InfluxSeriesResponse = BaseResponse & SignalsSeriesPayload;

// The worker's ToolLifeError.name (wire value set in signals_provider's errors.py) — not to
// be confused with the Python exception *class* name (MissingSignalDataError).
const MISSING_SIGNAL_DATA_ERROR = 'MissingSignalData';

/**
 * Fetch is manual: nothing runs until `query` transitions from null to a value (the
 * submitted-query pattern).
 */
export function useInfluxSeries(query: InfluxSeriesQuery | null) {
  const http = useApiClient();
  // Tracked locally (not through useApiCall's `error` string) so the empty-InfluxDB-window
  // case can be distinguished from every other failure without touching the shared hook.
  const [isEmptyWindow, setIsEmptyWindow] = useState(false);

  const apiFn = useCallback(
    async (_signal: AbortSignal) => {
      setIsEmptyWindow(false);
      try {
        const payload = await influxFetchSeries(query!, http ?? undefined);
        return {
          ok: true as const,
          data: { timestamp: new Date().toISOString(), ...payload },
        };
      } catch (err) {
        if (err instanceof Error && err.name === MISSING_SIGNAL_DATA_ERROR) {
          setIsEmptyWindow(true);
        }
        throw err;
      }
    },
    [query, http]
  );

  const { data, isLoading, error, retry } = useApiCall<InfluxSeriesResponse>({
    apiFn,
    immediate: query !== null,
    deps: [query],
  });

  return { data, isLoading, error, retry, isEmptyWindow };
}
